import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { KfComposeModal } from "./KfComposeModal";
import { KfPostPolicyModal, hasSeenKfPostPolicy } from "./KfPostPolicyModal";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Swords, Share2 } from "lucide-react";

const summaryLine = (s) => {
  const parts = [`${s.attack_count} attacks (${s.hits} hits${s.crits ? `, ${s.crits} crit${s.crits !== 1 ? "s" : ""}` : ""}, ${s.misses} misses)`];
  if (s.total_damage) parts.push(`${s.total_damage} total damage`);
  if (s.top_damage?.length) parts.push(`top: ${s.top_damage.map(([n, d]) => `${n} (${d})`).join(", ")}`);
  return parts.join(" - ");
};

// A Knight's permanent battle history - combat_logs itself is ephemeral (cleared after every
// fight so the next one starts fresh), this is the durable record they can browse and, if they
// want, turn into a Knights Fee brag post.
export function BattleLogPanel() {
  const { user } = useAuth();
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareTarget, setShareTarget] = useState(null);
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    api.get("/battle-log").then(({ data }) => setBattles(data)).catch(() => setBattles([])).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (battles.length === 0) return null;

  const openShare = (battle) => {
    if (!hasSeenKfPostPolicy(user?.id)) {
      setShareTarget(battle);
      setPolicyOpen(true);
      return;
    }
    setShareTarget(battle);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4 space-y-3" data-testid="battle-log-panel">
      <p className="text-xs uppercase tracking-widest text-[#D4AF37] flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> Battle Log</p>
      {battles.map((b) => (
        <div key={b.id} className="bg-black/20 border border-white/5 rounded-xl p-3" data-testid={`battle-log-entry-${b.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">{b.branch_name || b.game_name}</p>
              <p className="text-[10px] text-[#f1e0c5]/40">{new Date(b.ended_at).toLocaleDateString()}</p>
            </div>
            <Button data-testid={`battle-log-share-${b.id}`} size="sm" variant="secondary" className="rounded-full gap-1.5 text-xs h-7 shrink-0" onClick={() => openShare(b)}>
              <Share2 className="w-3.5 h-3.5" /> Share
            </Button>
          </div>
          <p className="text-xs text-[#f1e0c5]/60 mt-1.5">{summaryLine(b.summary)}</p>
        </div>
      ))}
      <KfPostPolicyModal open={policyOpen} onOpenChange={setPolicyOpen} userId={user?.id} onContinue={() => setPolicyOpen(false)} />
      <KfComposeModal
        open={!!shareTarget && !policyOpen}
        onOpenChange={(v) => !v && setShareTarget(null)}
        onPosted={() => setShareTarget(null)}
        initialText={shareTarget ? `Just survived a battle in ${shareTarget.branch_name || shareTarget.game_name}! ${summaryLine(shareTarget.summary)}` : ""}
      />
    </div>
  );
}
